<template>
  <div class="mandate-band">
    <div class="mandate-band__label">
      <div class="eyebrow-label">{{ $t("forwards.mandates.band_title") }}</div>
      <p v-if="bandNote" class="mandate-band__note mb-0">{{ bandNote }}</p>
    </div>
    <div
      v-for="column in columns"
      :key="column.quarter"
      class="mandate-band__column"
    >
      <div
        class="mandate-band__stack"
        role="img"
        :aria-label="column.ariaLabel"
      >
        <InfoTooltip
          v-for="part in column.parts"
          :key="part.kind"
          :text="part.tooltip"
          :showIcon="false"
          :style="{ height: part.height }"
        >
          <span
            class="mandate-band__part"
            :class="`mandate-band__part--${part.kind}`"
          ></span>
        </InfoTooltip>
      </div>
      <div class="mandate-band__quarter">
        {{ shortQuarterLabel(column.quarter) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { BandCell } from "@/forwards2/forwardsTypes";
import { shortQuarterLabel } from "@/forwards2/periodUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/** Top to bottom: the stack is drawn escrowed-at-the-bottom. */
const BAND_PART_KINDS = ["available", "committed", "escrowed"] as const;

interface BandPart {
  kind: (typeof BAND_PART_KINDS)[number];
  height: string;
  tooltip: string;
}

/**
 * The four vertical stacks above the mandate rows: everything the visible mandates list, sold and in
 * escrow, per quarter. Each part of a stack carries its own hover tooltip, like the parts of a mandate
 * row's cell; the stack's accessible label sums up the whole quarter.
 *
 * Stacks share one scale — the tallest drawn stack in the window — so a quarter's height is
 * comparable with its neighbours. The parts are drawn escrowed-at-the-bottom, since escrowed volume
 * is normally a part of committed volume and committed volume a part of listed; escrow a former
 * member left behind can exceed what is committed, and then the stack is taller than the listing.
 */
const props = defineProps<{
  band: BandCell[];
  /** Window quarter labels, in order; the band is padded to them when the API returns fewer. */
  windowQuarters: string[];
  bandNote: string | null;
}>();

const { t } = useI18n();

const cellsByQuarter = computed(
  () => new Map(props.band.map((cell) => [cell.quarter, cell]))
);

const scale = computed(() => Math.max(0, ...props.band.map(drawnTotal)));

const columns = computed(() =>
  props.windowQuarters.map((quarter) => {
    const cell = cellsByQuarter.value.get(quarter);
    const listed = cell?.listed ?? 0;
    const committed = cell?.committed ?? 0;
    const escrowed = cell?.escrowed ?? 0;
    const available = cell?.available ?? 0;

    const values = {
      quarter,
      listed: formatNumberWithSpaces(listed),
      committed: formatNumberWithSpaces(committed),
    };
    const volumes: Record<BandPart["kind"], number> = {
      available,
      committed: Math.max(0, committed - escrowed),
      escrowed,
    };
    const parts: BandPart[] = BAND_PART_KINDS.filter(
      (kind) => volumes[kind] > 0
    ).map((kind) => ({
      kind,
      height: height(volumes[kind]),
      tooltip: t(`forwards.mandates.band_${kind}`, {
        ...values,
        value: formatNumberWithSpaces(volumes[kind]),
      }),
    }));

    return {
      quarter,
      parts,
      ariaLabel: t("forwards.mandates.band_column", {
        ...values,
        escrowed: formatNumberWithSpaces(escrowed),
        available: formatNumberWithSpaces(available),
      }),
    };
  })
);

function height(value: number): string {
  return scale.value > 0 ? `${(value / scale.value) * 100}%` : "0%";
}

/** What the stack draws: available, the committed part not yet escrowed, and the escrowed part. */
function drawnTotal(cell: BandCell): number {
  return cell.available + Math.max(cell.committed, cell.escrowed);
}
</script>

<style scoped lang="scss">
@use "@/forwards2/forwards-colors" as colors;

.mandate-band {
  display: grid;
  grid-template-columns: 280px repeat(4, 1fr);
  gap: 0.25rem;
  align-items: end;
  padding-bottom: 0.5rem;
}

.mandate-band__label {
  padding-right: 0.75rem;
}

.mandate-band__note {
  font-size: 0.6875rem;
  color: var(--phoenix-gray-600);
}

.mandate-band__column {
  min-width: 0;
  padding: 0 0.125rem;
}

.mandate-band__stack {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 4.5rem;
  border-radius: 3px;
  background: colors.$forwards-track;
  overflow: hidden;
}

.mandate-band__part {
  display: block;
  width: 100%;
  height: 100%;
}

.mandate-band__part--available {
  background: colors.$forwards-available;
}

.mandate-band__part--committed {
  background: colors.$forwards-committed;
}

.mandate-band__part--escrowed {
  background: colors.$forwards-escrowed;
}

// InfoTooltip wraps its slot in an inline span; make it a block so the height binding applies, and
// keep a sliver of a tiny part tall enough to hover.
:deep(.info-tooltip-trigger) {
  display: block;
  min-height: 2px;
}

.mandate-band__quarter {
  font-size: 0.6875rem;
  color: var(--phoenix-gray-600);
  text-align: center;
  margin-top: 0.25rem;
}
</style>
